import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { EventCard, EventCardSkeleton } from '@/components/events/EventCard';
import { EventFilters } from '@/components/events/EventFilters';
import {
  CalendarRange,
  ChevronLeft,
  ChevronRight,
  Radio,
  Search,
} from 'lucide-react';
import {
  fetchClubs,
  fetchEvents,
  fetchRegionCities,
  fetchRegionCountries,
  fetchRegionProvinces,
  fetchRegionSubregions,
  fetchSports,
  friendlyError,
} from '@/lib/api';
import { toast } from 'sonner';
import { EVENTS as TID } from '@/constants/testIds';

const INITIAL_FILTERS = {
  sport: '',
  club: '',
  access_type: '',
  country: '',
  region: '',
  province: '',
  city: '',
};

const TABS = [
  { key: 'all', label: 'Todos', icon: CalendarRange },
  // Filtran por el nuevo modelo de 3 estados.
  { key: 'published', label: 'Publicados', icon: CalendarRange },
  { key: 'live_scheduled', label: 'Próximo directo', icon: Radio },
];

function useDebounced(value, delay = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

export default function EventsList() {
  // Permitimos preseleccionar filtros vía query params (?club=<slug>&status=published)
  // — usado por la ficha del club en "Ver todos los partidos". No cambia el
  // comportamiento cuando se navega a /partidos sin querystring.
  const [searchParams] = useSearchParams();
  const initialClub = searchParams.get('club') || '';
  const initialStatus = searchParams.get('status') || '';
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounced(query, 350);
  const [filters, setFilters] = useState({ ...INITIAL_FILTERS, club: initialClub });
  const [tab, setTab] = useState(
    initialStatus && ['all', 'published', 'live_scheduled'].includes(initialStatus)
      ? initialStatus
      : 'published'
  );
  const [page, setPage] = useState(1);
  const pageSize = 12;

  const [sports, setSports] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [countries, setCountries] = useState([]);
  const [regions, setRegions] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);

  const [data, setData] = useState({ items: [], total: 0, pages: 0, counts: {} });
  const [loading, setLoading] = useState(true);

  // Lista de clubes filtrada por el deporte seleccionado. Se apoya
  // exclusivamente en la relación existente club.sport_id → sport.id.
  // Si Deporte = Todos, mostramos todos los clubes. No hay nuevos endpoints.
  const filteredClubs = useMemo(() => {
    if (!filters.sport) return clubs;
    const matched = sports.find((s) => s.slug === filters.sport);
    if (!matched) return clubs;
    return clubs.filter((c) => c.sport_id === matched.id);
  }, [filters.sport, clubs, sports]);

  // Si al cambiar de deporte el club actualmente seleccionado ya no pertenece
  // a ese deporte, resetear Club a "Todos los clubes".
  //
  // IMPORTANTE: sólo validar cuando la lista de clubes ya está cargada.
  // De lo contrario, en el primer render (clubs=[]) se limpiaría un
  // `filters.club` preseleccionado desde la querystring (`?club=<slug>`)
  // antes de tener datos con los que comparar, rompiendo el enlace
  // "Ver todos los partidos" desde la ficha del club.
  useEffect(() => {
    if (!filters.club) return;
    if (!clubs.length) return;
    const stillValid = filteredClubs.some((c) => c.slug === filters.club);
    if (!stillValid) {
      setFilters((prev) => ({ ...prev, club: '' }));
      setPage(1);
    }
  }, [filteredClubs, filters.club, clubs.length]);

  // Catálogos iniciales
  useEffect(() => {
    (async () => {
      try {
        const [s, c, co] = await Promise.all([
          fetchSports(),
          fetchClubs({ page_size: 60 }),
          fetchRegionCountries(),
        ]);
        setSports(s);
        setClubs(c.items || []);
        setCountries(co);
      } catch (err) {
        toast.error(friendlyError(err));
      }
    })();
  }, []);

  // Cascadas territorio
  useEffect(() => {
    (async () => {
      try { setRegions(await fetchRegionSubregions(filters.country || undefined)); }
      catch { setRegions([]); }
    })();
  }, [filters.country]);
  useEffect(() => {
    (async () => {
      try {
        setProvinces(await fetchRegionProvinces({
          country: filters.country || undefined,
          region: filters.region || undefined,
        }));
      } catch { setProvinces([]); }
    })();
  }, [filters.country, filters.region]);
  useEffect(() => {
    (async () => {
      try {
        setCities(await fetchRegionCities({
          country: filters.country || undefined,
          region: filters.region || undefined,
          province: filters.province || undefined,
        }));
      } catch { setCities([]); }
    })();
  }, [filters.country, filters.region, filters.province]);

  // Cargar eventos
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const commonFilters = {
          q: debouncedQuery || undefined,
          sport: filters.sport || undefined,
          club: filters.club || undefined,
          access_type: filters.access_type || undefined,
          country: filters.country || undefined,
          region: filters.region || undefined,
          province: filters.province || undefined,
          city: filters.city || undefined,
        };
        // 3 fetches en paralelo: listado principal + conteo de cada uno
        // de los estados (Publicados / Próximo directo). Los backends de
        // fetchEvents devuelven `total` por consulta, así lo usamos como
        // contador de tab sin necesidad de nuevos endpoints.
        const [res, cntPub, cntLive] = await Promise.all([
          fetchEvents({
            ...commonFilters,
            status: tab === 'all' ? undefined : tab,
            page, page_size: pageSize, include_counts: true,
          }),
          fetchEvents({ ...commonFilters, status: 'published', page: 1, page_size: 1, include_counts: false }),
          fetchEvents({ ...commonFilters, status: 'live_scheduled', page: 1, page_size: 1, include_counts: false }),
        ]);
        if (!cancelled) {
          const merged = { ...res, counts: { ...(res.counts || {}), published: cntPub?.total || 0, live_scheduled: cntLive?.total || 0 } };
          setData(merged);
        }
      } catch (err) {
        if (!cancelled) toast.error(friendlyError(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [debouncedQuery, filters, tab, page]);

  const setFilter = (key, value) => {
    setFilters((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'country') { next.region = ''; next.province = ''; next.city = ''; }
      else if (key === 'region') { next.province = ''; next.city = ''; }
      else if (key === 'province') { next.city = ''; }
      return next;
    });
    setPage(1);
  };

  const clearAll = () => {
    setQuery('');
    setFilters(INITIAL_FILTERS);
    setPage(1);
  };

  const changeTab = (key) => {
    setTab(key);
    setPage(1);
  };

  const isEmpty = !loading && data.items.length === 0;
  const totalPages = data.pages || 0;
  const counts = data.counts || {};

  const resultsLabel = useMemo(() => {
    if (loading) return 'Cargando eventos…';
    if (data.total === 0) return 'Sin resultados';
    if (data.total === 1) return '1 evento encontrado';
    return `${data.total} eventos encontrados`;
  }, [loading, data.total]);

  return (
    <MainLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12" data-testid={TID.page}>
        <header className="mb-8 sm:mb-10">
          <p className="text-[11px] uppercase tracking-[0.24em] text-[var(--ns-gold-700)] font-semibold">
            Calendario OTT
          </p>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl text-[var(--ns-text)] leading-tight">
            Partidos
          </h1>
          <p className="mt-3 text-[var(--ns-text-muted)] max-w-2xl text-sm sm:text-base leading-relaxed">
            Próximos partidos, directos, replays y resúmenes del deporte base.
            Filtra por deporte, club, fecha y territorio para encontrar lo que más
            te interesa.
          </p>
        </header>

        {/* Tabs */}
        <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 mb-6">
          <div className="inline-flex items-center gap-1.5 rounded-2xl bg-white border border-[var(--ns-border)] p-1.5 shadow-[var(--ns-shadow-soft)]">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.key;
              const count = t.key === 'all' ? counts.all : counts[t.key];
              return (
                <button
                  key={t.key}
                  onClick={() => changeTab(t.key)}
                  data-testid={TID.tab(t.key)}
                  className={[
                    'inline-flex items-center gap-1.5 h-9 px-3 sm:px-4 rounded-xl text-sm font-semibold whitespace-nowrap transition-colors',
                    active
                      ? 'bg-gradient-to-r from-[var(--ns-gold)] to-[var(--ns-gold-600)] text-[#1c1612] shadow-sm'
                      : 'text-[var(--ns-text-muted)] hover:text-[var(--ns-text)] hover:bg-[var(--ns-bg-2)]',
                  ].join(' ')}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {t.label}
                  {typeof count === 'number' ? (
                    <Badge
                      variant="secondary"
                      className={
                        active
                          ? 'bg-black/10 text-[#1c1612] border-0 ml-0.5 text-[10px] px-1.5 py-0'
                          : 'bg-[var(--ns-bg-2)] text-[var(--ns-text-muted)] border-0 ml-0.5 text-[10px] px-1.5 py-0'
                      }
                    >
                      {count}
                    </Badge>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-12 gap-8">
          <aside className="sm:col-span-4 lg:col-span-3">
            <EventFilters
              query={query}
              setQuery={setQuery}
              filters={filters}
              setFilter={setFilter}
              clearAll={clearAll}
              sports={sports}
              clubs={filteredClubs}
              countries={countries}
              regions={regions}
              provinces={provinces}
              cities={cities}
            />
          </aside>

          <section className="sm:col-span-8 lg:col-span-9">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-[var(--ns-text-muted)]">{resultsLabel}</span>
              {totalPages > 1 ? (
                <span className="text-xs text-[var(--ns-text-subtle)]">
                  Página {data.page || page} de {totalPages}
                </span>
              ) : null}
            </div>

            {loading ? (
              <div
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6"
                data-testid={TID.loadingSkeleton}
              >
                {Array.from({ length: 6 }).map((_, i) => (
                  <EventCardSkeleton key={i} />
                ))}
              </div>
            ) : isEmpty ? (
              <div
                className="rounded-2xl border border-dashed border-[var(--ns-border)] bg-white p-10 text-center"
                data-testid={TID.emptyState}
              >
                <div className="h-14 w-14 mx-auto rounded-2xl bg-[var(--ns-gold)]/15 text-[var(--ns-gold-700)] inline-flex items-center justify-center mb-4">
                  {query ? <Search className="h-6 w-6" /> : <CalendarRange className="h-6 w-6" />}
                </div>
                <h3 className="font-display text-lg text-[var(--ns-text)]">
                  Sin eventos en este criterio
                </h3>
                <p className="mt-1 text-sm text-[var(--ns-text-muted)] max-w-md mx-auto">
                  Prueba a cambiar la pestaña, quitar algún filtro o ampliar la búsqueda.
                  Más eventos llegarán pronto a NetScouters.
                </p>
                <Button
                  className="mt-5 h-10 rounded-xl bg-gradient-to-r from-[var(--ns-gold)] to-[var(--ns-gold-600)] text-[#1c1612] font-semibold border-0 hover:from-[var(--ns-gold-600)] hover:to-[var(--ns-gold-700)] hover:text-white transition-all"
                  onClick={clearAll}
                >
                  Limpiar filtros
                </Button>
              </div>
            ) : (
              <div
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6"
                data-testid={TID.grid}
              >
                {data.items.map((ev) => (
                  <EventCard key={ev.id} event={ev} />
                ))}
              </div>
            )}

            {totalPages > 1 ? (
              <div
                className="mt-8 flex items-center justify-between gap-4"
                data-testid={TID.pagination}
              >
                <Button
                  type="button"
                  variant="outline"
                  className="h-10 rounded-xl border-[var(--ns-border)] disabled:opacity-50"
                  disabled={page <= 1 || loading}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" /> Anterior
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="h-10 rounded-xl border-[var(--ns-border)] disabled:opacity-50"
                  disabled={page >= totalPages || loading}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  Siguiente <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            ) : null}
          </section>
        </div>
      </div>
    </MainLayout>
  );
}
